import { PayloadAction } from '@reduxjs/toolkit'
import {
    StudioState,
    AssetItem,
    LayoutItem,
    ActiveLayoutItem,
    PageData,
} from '../initialState'

const getActivePage = (state: StudioState): PageData | null => {
    return state.pages[state.activePageId] || null
}

export const itemReducers = {
    itemAdded: (
        state: StudioState,
        action: PayloadAction<{
            pageId: string
            asset: Omit<AssetItem, 'id'>
            desktopLayout: Omit<LayoutItem, 'id' | 'assetId'>
            mobileLayout: Omit<LayoutItem, 'id' | 'assetId'>
            assetId: string
            instanceId: string
        }>,
    ) => {
        const {
            pageId,
            asset,
            desktopLayout,
            mobileLayout,
            assetId,
            instanceId,
        } = action.payload

        const page = state.pages[pageId]
        if (!page) {
            console.error(`Page with id ${pageId} not found.`)
            return
        }

        page.assets[assetId] = { ...asset, id: assetId }

        const maxZIndex =
            Math.max(
                0,
                ...Object.values(page.layouts.desktop.items).map(
                    (i) => i.zIndex,
                ),
            ) + 1

        const desktopItem: LayoutItem = {
            ...desktopLayout,
            id: instanceId,
            assetId,
            zIndex: maxZIndex,
        }

        page.layouts.desktop.items[instanceId] = desktopItem
        page.layouts.desktop.itemIds.push(instanceId)

        const mobileItem: LayoutItem = {
            ...mobileLayout,
            id: instanceId,
            assetId,
            zIndex: maxZIndex,
        }

        page.layouts.mobile.items[instanceId] = mobileItem
        page.layouts.mobile.itemIds.push(instanceId)
    },

    updateItem: (
        state: StudioState,
        action: PayloadAction<{
            pageId: string
            updatedItem: ActiveLayoutItem
        }>,
    ) => {
        const { pageId, updatedItem } = action.payload
        const { id, assetId, content, styles } = updatedItem

        const page = state.pages[pageId]
        if (!page) {
            return
        }

        if (page.assets[assetId]) {
            page.assets[assetId].content = content
        }

        const activeLayout = page.layouts[state.responsiveMode]
        if (activeLayout.items[id]) {
            activeLayout.items[id].styles = styles
        }
    },

    itemDeleted: (
        state: StudioState,
        // --- MODIFIED: Payload no longer needs itemId ---
        action: PayloadAction<{ pageId: string }>,
    ) => {
        const { pageId } = action.payload
        const page = getActivePage(state)

        if (
            !page ||
            !page.selectedItemIds ||
            page.selectedItemIds.length === 0
        ) {
            return
        }

        const assetIdsToDelete = new Set<string>()
        const itemIdsToDelete = [...page.selectedItemIds]

        itemIdsToDelete.forEach((itemId) => {
            const itemToDelete = page.layouts.desktop.items[itemId]
            if (itemToDelete) {
                assetIdsToDelete.add(itemToDelete.assetId)
            }

            delete page.layouts.desktop.items[itemId]
            delete page.layouts.mobile.items[itemId]
        })

        page.layouts.desktop.itemIds = page.layouts.desktop.itemIds.filter(
            (id) => !itemIdsToDelete.includes(id),
        )
        page.layouts.mobile.itemIds = page.layouts.mobile.itemIds.filter(
            (id) => !itemIdsToDelete.includes(id),
        )

        // Check if asset is still in use
        const allRemainingItems = [
            ...Object.values(page.layouts.desktop.items),
            ...Object.values(page.layouts.mobile.items),
        ]

        assetIdsToDelete.forEach((assetId) => {
            const isAssetStillInUse = allRemainingItems.some(
                (item) => item.assetId === assetId,
            )

            if (!isAssetStillInUse) {
                delete page.assets[assetId]
            }
        })

        page.selectedItemIds = []
    },

    itemSelected: (
        state: StudioState,
        action: PayloadAction<{
            pageId: string
            itemId: string
            metaKey?: boolean
            shiftKey?: boolean
        }>,
    ) => {
        const { itemId, metaKey } = action.payload
        const activePage = getActivePage(state)
        if (!activePage) return

        if (!activePage.selectedItemIds) {
            activePage.selectedItemIds = []
        }
        const isSelected = activePage.selectedItemIds.includes(itemId)

        if (metaKey) {
            if (isSelected) {
                activePage.selectedItemIds = activePage.selectedItemIds.filter(
                    (id) => id !== itemId,
                )
            } else {
                activePage.selectedItemIds.push(itemId)
            }
        } else {
            if (isSelected && activePage.selectedItemIds.length === 1) {
                return
            }
            activePage.selectedItemIds = [itemId]
        }
    },

    itemDeselected: (
        state: StudioState,
        action: PayloadAction<{ pageId: string }>,
    ) => {
        const activePage = getActivePage(state)
        if (activePage) {
            activePage.selectedItemIds = []
        }
    },
}
